import React from 'react';
import classNames from 'classnames';
import { Flex, TimePicker } from 'antd';
import { ClockCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { utcToZonedTime } from 'date-fns-tz';
import { DateFormats } from 'infrastructure/enums';

import s from './styles.module.scss';

import type { Dayjs } from 'dayjs';
import type { ICommonInputProps } from 'infrastructure/interfaces';

dayjs.extend(customParseFormat);
export type TOnCalendarChange = (
  date: Dayjs | Dayjs[] | undefined,
  dateString: string | string[],
) => void;

interface IBaseInputProps extends ICommonInputProps<string> {
  format?: DateFormats;
  onChange?: (dateString: string) => void;
  onCalendarChange?: TOnCalendarChange;
  disablePastTimes?: boolean;
  selectedDate?: string | Date;
  timezone?: string;
  onNowClick?: (now: Dayjs) => void;
}

const BaseTimePicker: React.FC<IBaseInputProps> = (props) => {
  const {
    value,
    disabled,
    plaintext,
    readonly,
    invalid,
    onChange,
    className,
    style,
    placeholder = '',
    format = DateFormats['h:mm a'],
    onCalendarChange,
    disablePastTimes = false,
    selectedDate,
    timezone,
    onNowClick,
  } = props;

  const selectBoxClassNames = classNames(
    {
      [s.readonly]: readonly,
    },
    className,
  );

  const disabledTime = React.useMemo(() => {
    return () => {
      if (!disablePastTimes) return {};

      const now = timezone
        ? (() => {
            Intl.DateTimeFormat(undefined, { timeZone: timezone });
            return dayjs(utcToZonedTime(new Date(), timezone));
          })()
        : dayjs();

      const getDisabledTimeForCurrentTime = () => {
        const currentHour = now.hour();
        const currentMinute = now.minute();

        return {
          disabledHours: () => {
            const hours = [];
            for (let i = 0; i < currentHour; i++) {
              hours.push(i);
            }
            return hours;
          },
          disabledMinutes: (selectedHour: number) => {
            if (selectedHour === currentHour) {
              const minutes = [];
              for (let i = 0; i <= currentMinute; i++) {
                minutes.push(i);
              }
              return minutes;
            }
            return [];
          },
        };
      };

      if (!selectedDate) {
        return getDisabledTimeForCurrentTime();
      }

      const parsedDate = dayjs(selectedDate);
      if (!parsedDate.isValid()) {
        return {};
      }

      const isBeforeToday = parsedDate.isBefore(now, 'day');
      const isToday = parsedDate.isSame(now, 'day');

      if (isBeforeToday) {
        const allHours = Array.from({ length: 24 }, (_, i) => i);
        const allMinutes = Array.from({ length: 60 }, (_, i) => i);
        return {
          disabledHours: () => allHours,
          disabledMinutes: () => allMinutes,
        };
      }

      if (!isToday) {
        return {};
      }

      return getDisabledTimeForCurrentTime();
    };
  }, [disablePastTimes, selectedDate, timezone]);

  const getNowInTimezone = React.useCallback(() => {
    return timezone ? dayjs(utcToZonedTime(new Date(), timezone)) : dayjs();
  }, [timezone]);

  const handleNowClick = React.useCallback(() => {
    const now = getNowInTimezone();
    onChange?.(now.format(DateFormats['HH:mm']));
    onNowClick?.(now);
  }, [getNowInTimezone, onChange, onNowClick]);

  return (
    <TimePicker
      use12Hours
      format={DateFormats['h:mm a']}
      style={{ width: '100%', ...style }}
      placeholder={placeholder}
      value={value ? dayjs(value, format) : undefined}
      disabled={disabled}
      className={selectBoxClassNames}
      onChange={(time, dateString) => {
        if (onChange && !Array.isArray(dateString) && time)
          onChange(time.format(DateFormats['HH:mm']));
      }}
      variant={plaintext ? 'borderless' : 'outlined'}
      status={invalid ? 'error' : undefined}
      suffixIcon={plaintext ? <></> : <ClockCircleOutlined />}
      onCalendarChange={onCalendarChange}
      disabledTime={disablePastTimes ? disabledTime : undefined}
      needConfirm={false}
      showNow={false}
      renderExtraFooter={() => (
        <Flex justify="center">
          <button
            type="button"
            className="ant-picker-now-btn"
            onClick={handleNowClick}
          >
            Now
          </button>
        </Flex>
      )}
    />
  );
};

export const BaseTimePickerDisplayName = 'BaseTimePicker';
BaseTimePicker.displayName = BaseTimePickerDisplayName;

export default BaseTimePicker;
